<script setup lang="ts">
/**
 * Temporadas del centro (§24).
 *
 * El nombre y las fechas se cargan **una sola vez** acá, y después cada
 * servicio elige en qué temporada corre. Antes eran copias sueltas dentro de
 * cada offering: nada garantizaba que las fechas coincidieran, corregir una
 * significaba corregirlas todas, y no había forma de ver una temporada como tal.
 *
 * Formato de listado, como los días de cierre: es la misma clase de dato —unas
 * pocas filas con un rango de fechas— y verlas juntas es justamente lo que
 * antes no se podía.
 */
import { computed, onMounted, ref } from 'vue'
import { listSeasons } from '@/api/seasons'
import { ApiError } from '@/api/client'
import AlertBox from '@/components/AlertBox.vue'
import SeasonDrawer from '@/components/SeasonDrawer.vue'
import { usePermissions } from '@/composables/usePermissions'
import { notifySuccess, notifyWarning } from '@/composables/useSnackbar'
import { overlappingSeasons, seasonDateRange, seasonNameList } from '@/data/offeringHelpers'
import type { ScheduleImpact, Season, SeasonStatus } from '@/types'

definePage({ meta: { requiresAuth: true } })

const route = useRoute('dive-centers-id-offerings-seasons')
const router = useRouter()
const id = String(route.params.id)

/*
 * Las temporadas las define quien configura el centro. `staff` las ve —saber en
 * qué temporada está el centro hoy es parte de contestar en el mostrador— pero
 * no las crea ni las edita.
 */
const { canConfigureCenter } = usePermissions()

const seasons = ref<Season[]>([])
const loading = ref(true)
const error = ref('')

const editDrawer = ref(false)
const editing = ref<Season | null>(null)

const STATUS_CHIP: Record<SeasonStatus, { label: string; color: string }> = {
  upcoming: { label: 'Upcoming', color: 'info' },
  running: { label: 'Running', color: 'success' },
  finished: { label: 'Finished', color: 'secondary' },
}

const headers = [
  { title: 'Season', key: 'name' },
  { title: 'Dates', key: 'dates', sortable: false },
  { title: 'Status', key: 'status', sortable: false },
  { title: 'Services', key: 'offeringCount', sortable: false },
  { title: '', key: 'actions', sortable: false, width: 90, align: 'end' as const },
]

async function load() {
  loading.value = true
  error.value = ''
  try {
    seasons.value = (await listSeasons(id)).seasons
  }
  catch (e) {
    error.value = e instanceof ApiError ? e.message : 'Could not load the seasons.'
  }
  finally {
    loading.value = false
  }
}

onMounted(load)

function openSeason(season: Season | null) {
  editing.value = season
  editDrawer.value = true
}

/*
 * Los servicios de una temporada son una PANTALLA, no un panel.
 *
 * Es un listado con las mismas columnas que Services —categoría, precio, días—
 * y eso no entra en una franja lateral: había que scrollear a ciegas para ver
 * qué estaba cargado. El panel queda para editar un servicio puntual.
 */
function openServices(season: Season) {
  router.push({
    name: 'dive-centers-id-offerings-seasons-season-id',
    params: { id, seasonId: season.id },
  })
}

/**
 * Lo que le pasó al calendario, dicho.
 *
 * `detached` es el número que importa: son salidas futuras CON gente anotada
 * que la regla nueva ya no produce. No se borran solas — quedan marcadas y las
 * resuelve el provider — pero si no se dicen, aparecen en ámbar en el
 * calendario sin ninguna explicación de por qué.
 */
/*
 * Con qué otras temporadas se pisa cada una.
 *
 * El aviso existía solo al cargarla, y ahí llega tarde para todo lo que ya está
 * guardado: quien abre esta pantalla ve dos filas con fechas que se superponen y
 * nada que se lo diga — justamente la situación en la que el aviso sirve, porque
 * es cuando hay que decidir si estaba bien. Se marca la fila y el `title` nombra
 * contra cuál choca, que es lo que hace accionable el aviso.
 *
 * Mismo criterio que el panel (`overlappingSeasons`), y no una copia: si una de
 * las dos afinara la regla, las dos pantallas dirían cosas distintas sobre los
 * mismos datos.
 *
 * Se calcula sobre la lista entera y no por fila con un método en el template:
 * eso sería N² recorridos en cada redibujo de la tabla.
 */
const overlapsBySeason = computed(() => {
  const map = new Map<string, string>()

  for (const season of seasons.value) {
    const others = overlappingSeasons(season, seasons.value, season.id)

    if (others.length)
      map.set(season.id, seasonNameList(others.map(o => `"${o.name}"`)))
  }

  return map
})

async function onSaved(impact: ScheduleImpact | null) {
  await load()

  if (impact?.detached) {
    notifyWarning(
      `${impact.detached} activit${impact.detached === 1 ? 'y' : 'ies'} with divers booked `
      + 'no longer match the schedule. Review them in Plans & Bookings.',
    )

    return
  }

  notifySuccess('Season saved.')
}
</script>

<template>
  <div>
    <div
      v-if="canConfigureCenter"
      class="d-flex justify-end mb-6"
    >
      <VBtn
        prepend-icon="ri-add-line"
        @click="openSeason(null)"
      >
        New season
      </VBtn>
    </div>

    <AlertBox
      variant="danger"
      :message="error"
    />

    <VCard>
      <VDataTable
        :items="seasons"
        item-value="id"
        :headers="headers"
        :loading="loading"
        class="text-no-wrap rounded-0"
      >
        <template #item.name="{ item }">
          <span class="text-body-1 font-weight-medium">{{ item.name }}</span>
        </template>

        <template #item.dates="{ item }">
          {{ seasonDateRange(item.from, item.to) }}

          <!--
            Ícono y no un chip: la columna es la fecha, y lo que hay que decir es
            "ojo con ESTE rango". Un chip al lado competiría con el de Status, que
            sí es un estado. Ámbar porque avisa, no prohíbe — solapar es legítimo.
          -->
          <VTooltip
            v-if="overlapsBySeason.get(item.id)"
            :text="`These dates overlap ${overlapsBySeason.get(item.id)}. That's allowed — just make sure it's what you meant, since a service can't have a schedule in two seasons that share dates.`"
            location="top"
            max-width="320"
          >
            <template #activator="{ props: tip }">
              <VIcon
                v-bind="tip"
                icon="ri-error-warning-line"
                size="16"
                color="warning"
                class="ms-2"
              />
            </template>
          </VTooltip>
        </template>

        <template #item.status="{ item }">
          <VChip
            :color="STATUS_CHIP[item.status].color"
            size="small"
            label
          >
            {{ STATUS_CHIP[item.status].label }}
          </VChip>
        </template>

        <!-- El número es un botón: es el camino para gestionar la relación
             desde este lado, y esconderlo en el menú de los tres puntos lo
             volvería el camino secundario. -->
        <template #item.offeringCount="{ item }">
          <!-- Para staff es solo el número: la pantalla a la que lleva es de
               configuración y el guard lo rebota. -->
          <VBtn
            v-if="canConfigureCenter"
            variant="text"
            size="small"
            color="primary"
            class="px-1"
            @click="openServices(item)"
          >
            {{ item.offeringCount || 'Add services' }}
            <template v-if="item.offeringCount">
              service{{ item.offeringCount === 1 ? '' : 's' }}
            </template>
          </VBtn>
          <span
            v-else
            class="text-body-2"
          >
            {{ item.offeringCount }}
          </span>
        </template>

        <template #item.actions="{ item }">
          <VBtn
            v-if="canConfigureCenter"
            icon="ri-pencil-line"
            variant="text"
            size="small"
            color="default"
            title="Edit season"
            @click="openSeason(item)"
          />
        </template>

        <template #no-data>
          <div class="py-8 text-center">
            <p class="text-body-1 mb-1">
              No seasons yet
            </p>
            <p class="text-body-2 text-medium-emphasis mb-0">
              A season is a stretch of the year with its own dates — "Summer 2026-2027".
              Load it once here and every service can run in it.
            </p>
          </div>
        </template>
      </VDataTable>
    </VCard>

    <SeasonDrawer
      v-model="editDrawer"
      :dive-center-id="id"
      :editing="editing"
      :existing="seasons"
      @saved="onSaved"
    />

  </div>
</template>
